import type { DeskPackageConfiguration } from '../types/rate-card';

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateKey(value: string): Date {
  const match = DATE_KEY.exec(value);
  if (!match) throw new Error(`Invalid booking date: ${value}`);
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (
    date.getUTCFullYear() !== Number(match[1]) ||
    date.getUTCMonth() !== Number(match[2]) - 1 ||
    date.getUTCDate() !== Number(match[3])
  ) {
    throw new Error(`Invalid booking date: ${value}`);
  }
  return date;
}

function dateKey(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

function addDays(date: Date, count: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + count);
  return next;
}

function addMonthsClamped(date: Date, count: number): Date {
  const targetMonth = date.getUTCMonth() + count;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
  return new Date(Date.UTC(targetYear, normalizedMonth, Math.min(date.getUTCDate(), lastDay)));
}

/** Inclusive final date for a package that starts on `startDate`. */
export function bookingEndDate(
  startDate: string,
  duration: DeskPackageConfiguration['duration'] | null,
): string {
  const start = parseDateKey(startDate);
  if (!duration) return startDate;
  if (!Number.isInteger(duration.count) || duration.count < 1) {
    throw new Error('A booking duration must be a positive whole number');
  }

  const exclusiveEnd =
    duration.unit === 'month'
      ? addMonthsClamped(start, duration.count)
      : addDays(start, duration.count * (duration.unit === 'week' ? 7 : 1));
  return dateKey(addDays(exclusiveEnd, -1));
}
